"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Explicit-render Cloudflare Turnstile widget (PRD §5.1.2). Renders only when
 * NEXT_PUBLIC_TURNSTILE_SITE_KEY is set; otherwise shows a dev note and the form
 * submits with a null token (the server skips verification when unconfigured).
 */
interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      theme?: "dark" | "light" | "auto";
      callback: (token: string) => void;
      "expired-callback"?: () => void;
      "error-callback"?: () => void;
    },
  ) => string;
  reset: (id?: string) => void;
  remove?: (id?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loadPromise: Promise<void> | null = null;

function loadTurnstile(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (loadPromise) return loadPromise;
  loadPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Turnstile failed to load"));
    document.head.appendChild(s);
  });
  return loadPromise;
}

export function Turnstile({
  onToken,
  resetSignal = 0,
}: {
  onToken: (token: string | null) => void;
  /**
   * Bump to reset the widget. A Turnstile token is SINGLE-USE: once the server has
   * verified it (even for a submission it then rejected), a retry with the same token
   * fails "Captcha verification failed" forever. Forms bump this after every failed POST.
   */
  resetSignal?: number;
}) {
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
  const ref = useRef<HTMLDivElement>(null);
  const rendered = useRef(false);
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    if (!siteKey || rendered.current) return;
    let cancelled = false;
    loadTurnstile()
      .then(() => {
        if (cancelled || rendered.current || !ref.current || !window.turnstile) return;
        rendered.current = true;
        widgetId.current = window.turnstile.render(ref.current, {
          sitekey: siteKey,
          theme: "dark",
          callback: (token) => onToken(token),
          "expired-callback": () => onToken(null),
          "error-callback": () => onToken(null),
        });
      })
      .catch(() => onToken(null));
    return () => {
      cancelled = true;
    };
  }, [siteKey, onToken]);

  // Forget the widget when it unmounts (e.g. the contact modal closes), so a reopened
  // form renders a fresh one instead of reusing a spent token.
  useEffect(() => {
    return () => {
      if (widgetId.current && window.turnstile?.remove) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch {
          /* already gone */
        }
      }
      widgetId.current = null;
      rendered.current = false;
    };
  }, []);

  useEffect(() => {
    if (resetSignal === 0) return;
    onToken(null);
    if (window.turnstile && widgetId.current) {
      try {
        window.turnstile.reset(widgetId.current);
      } catch {
        /* widget already gone */
      }
    }
  }, [resetSignal, onToken]);

  if (!siteKey) {
    return (
      <p className="text-xs text-text-muted">
        Captcha is disabled in this environment.
      </p>
    );
  }
  return <div ref={ref} />;
}

/**
 * Captcha state for a submit form. `getToken()` waits briefly for the widget instead of
 * submitting a null token while Turnstile is still working (a fast click right after the
 * page loads), and `reset()` re-arms the single-use token after a failed submission.
 */
export function useCaptcha() {
  const [resetSignal, setResetSignal] = useState(0);
  const tokenRef = useRef<string | null>(null);
  const onToken = useCallback((t: string | null) => {
    tokenRef.current = t;
  }, []);
  const reset = useCallback(() => setResetSignal((n) => n + 1), []);
  /** Take the token for ONE request: it's cleared and the widget re-armed immediately. */
  const getToken = useCallback(async (waitMs = 8000): Promise<string | null> => {
    if (!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY) return null;
    const until = Date.now() + waitMs;
    while (!tokenRef.current && Date.now() < until) {
      await new Promise((r) => setTimeout(r, 200));
    }
    const token = tokenRef.current;
    tokenRef.current = null;
    if (token) reset();
    return token;
  }, [reset]);
  const widget = <Turnstile onToken={onToken} resetSignal={resetSignal} />;
  return { widget, getToken, reset };
}
